import React from 'react';

const money = (value) => `Rs. ${parseFloat(value || 0).toFixed(2)}`;

const dateParts = (value) => {
  if (!value) return { date: '', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  return {
    date: d.toLocaleDateString('en-GB'),
    time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
  };
};

const rowStyle = { display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '11px' };
const boldRowStyle = { ...rowStyle, fontWeight: 700, fontSize: '12px' };
const dividerStyle = { borderTop: '1px dashed #111', margin: '8px 0' };
const solidDivider = { borderTop: '1px solid #111', margin: '4px 0' };

const tableHeaderCell = {
  fontSize: '8px', fontWeight: 700, paddingBottom: '3px', borderBottom: '1px solid #D1D5DB',
};
const gstHeaderCell = {
  fontSize: '7.5px', fontWeight: 700, paddingBottom: '3px', borderBottom: '1px solid #D1D5DB',
};

const FONT_SCALE = { small: 0.9, normal: 1, large: 1.15 };
// 80mm roll paper only has ~72mm of printable width (576 dots @ 203dpi);
// anything wider is clipped on the right. Divide by zoom so the scaled
// receipt still lands at exactly the printable width.
const PRINTABLE_WIDTH_MM = 72;

function ItemsTable({ items, amountLabel = 'Amount', showRate = true, showAmt = true, decimals = 2, rateLabel = 'Rate' }) {
  // Hiding a column returns its width to Item rather than leaving blank
  // gutter -- 18%/19% (Rate/Amount) folded back into Item's base 50% as needed.
  const itemColWidth = `${50 + (showRate ? 0 : 18) + (showAmt ? 0 : 19)}%`;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
      <thead>
        <tr>
          <th style={{ ...tableHeaderCell, textAlign: 'left', width: '5%' }}>Sr</th>
          <th style={{ ...tableHeaderCell, textAlign: 'left', width: itemColWidth }}>Item</th>
          <th style={{ ...tableHeaderCell, textAlign: 'center', width: '8%' }}>Qty</th>
          {showRate && <th style={{ ...tableHeaderCell, textAlign: 'right', width: '18%' }}>{rateLabel}</th>}
          {showAmt && <th style={{ ...tableHeaderCell, textAlign: 'right', width: '19%' }}>{amountLabel}</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={`${item.index}-${item.name}`}>
            <td style={{ verticalAlign: 'top', paddingTop: '4px' }}>{item.index}</td>
            <td style={{ verticalAlign: 'top', paddingTop: '4px' }}>
              <div style={{ fontWeight: 600, lineHeight: 1.3 }}>{item.name}</div>
              {item.hsn && <div style={{ fontSize: '9px' }}>HSN: {item.hsn}</div>}
              {item.reason && <div style={{ fontSize: '9px', fontStyle: 'italic' }}>({item.reason})</div>}
            </td>
            <td style={{ textAlign: 'center', verticalAlign: 'top', paddingTop: '4px' }}>{item.quantity}</td>
            {showRate && (
              <td style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: '4px' }}>
                {parseFloat(item.rate || 0).toFixed(decimals)}
              </td>
            )}
            {showAmt && (
              <td style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: '4px' }}>
                {parseFloat(item.amount || 0).toFixed(decimals)}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GstTable({ gstSummary }) {
  if (!gstSummary || gstSummary.length === 0) return null;
  return (
    <>
      <div style={dividerStyle} />
      <div style={{ fontSize: '10px', fontWeight: 700, marginBottom: '3px' }}>GST Summary</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
        <thead>
          <tr>
            <th style={{ ...gstHeaderCell, textAlign: 'left', width: '24%' }}>Tax</th>
            <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>Taxable</th>
            <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>CGST</th>
            <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>SGST</th>
            <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>IGST</th>
          </tr>
        </thead>
        <tbody>
          {gstSummary.map((row) => (
            <tr key={`${row.label}-${row.taxableAmount}`}>
              <td style={{ paddingTop: '3px' }}>{row.label}</td>
              <td style={{ textAlign: 'right', paddingTop: '3px' }}>{row.taxableAmount.toFixed(2)}</td>
              <td style={{ textAlign: 'right', paddingTop: '3px' }}>{row.cgstAmount.toFixed(2)}</td>
              <td style={{ textAlign: 'right', paddingTop: '3px' }}>{row.sgstAmount.toFixed(2)}</td>
              <td style={{ textAlign: 'right', paddingTop: '3px' }}>{row.igstAmount.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

const ThermalExchangeReceipt80mm = ({ receipt }) => {
  const { date, time } = dateParts(receipt?.completedAt);
  const isEven = receipt?.settlementDirection === 'even';
  const isRefund = receipt?.settlementDirection === 'customer_refund';
  const showRate = receipt?.showRateColumn !== false;
  const showAmt = receipt?.showAmountColumn !== false;
  const decimals = receipt?.itemAmountDecimalPlaces || 2;
  const rateLabel = receipt?.rateColumnLabel || 'Rate';
  const zoom = FONT_SCALE[receipt?.receiptFontSize] ?? FONT_SCALE.normal;

  return (
    <div
      style={{
        width: `${PRINTABLE_WIDTH_MM / zoom}mm`,
        color: '#111',
        fontFamily: 'monospace',
        padding: '8px 4px',
        boxSizing: 'border-box',
        zoom,
      }}
    >
      {/* Store / Org header */}
      <div style={{ textAlign: 'center' }}>
        {receipt.logoUrl && (
          <img
            src={receipt.logoUrl}
            alt=""
            style={{ maxWidth: '120px', maxHeight: '60px', objectFit: 'contain', marginBottom: '4px', filter: 'grayscale(1) contrast(1.3)' }}
          />
        )}
        <div style={{ fontSize: '18px', fontWeight: 700 }}>{receipt.storeName}</div>
        {receipt.organizationName && (
          <div style={{ fontSize: '12px', marginTop: '4px' }}>{receipt.organizationName}</div>
        )}
        {receipt.storeAddress && <div style={{ fontSize: '10px' }}>{receipt.storeAddress}</div>}
        {receipt.storePhone && <div style={{ fontSize: '11px' }}>Tel: {receipt.storePhone}</div>}
        {receipt.gstin && <div style={{ fontSize: '11px' }}>GSTIN: {receipt.gstin}</div>}
      </div>

      <div style={dividerStyle} />
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '14px' }}>EXCHANGE RECEIPT</div>
      <div style={dividerStyle} />

      {/* Exchange meta */}
      <div style={{ fontSize: '11px' }}>
        {receipt.exchangeNumber && (
          <div style={rowStyle}><span>Exchange #</span><span>{receipt.exchangeNumber}</span></div>
        )}
        <div style={rowStyle}><span>Original Order</span><span>{receipt.originalOrderNumber}</span></div>
        <div style={rowStyle}><span>Date</span><span>{date}</span></div>
        <div style={rowStyle}><span>Time</span><span>{time}</span></div>
        {receipt.cashierName && (
          <div style={rowStyle}><span>Cashier</span><span>{receipt.cashierName}</span></div>
        )}
        {receipt.terminalName && (
          <div style={rowStyle}><span>Terminal</span><span>{receipt.terminalName}</span></div>
        )}
      </div>

      {/* Customer */}
      {(receipt.customerName || receipt.customerMobile) && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px' }}>
            {receipt.customerName && <div>Customer: {receipt.customerName}</div>}
            {receipt.customerMobile && <div>Mobile: {receipt.customerMobile}</div>}
            {receipt.customerGstin && <div>GSTIN: {receipt.customerGstin}</div>}
          </div>
        </>
      )}

      {/* ── RETURN / CREDIT NOTE section ── */}
      <div style={dividerStyle} />
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '11px' }}>
        --- RETURN / CREDIT NOTE ---
      </div>
      {receipt.creditNoteNumber && (
        <div style={{ fontSize: '11px', marginTop: '4px' }}>
          <div style={rowStyle}><span>Credit Note</span><span>{receipt.creditNoteNumber}</span></div>
          {receipt.returnNumber && (
            <div style={rowStyle}><span>Return #</span><span>{receipt.returnNumber}</span></div>
          )}
        </div>
      )}

      <div style={solidDivider} />
      <ItemsTable items={receipt.returnItems || []} amountLabel="Refund" showRate={showRate} decimals={decimals} rateLabel={rateLabel} />
      <GstTable gstSummary={receipt.returnGstSummary} />

      <div style={dividerStyle} />
      <div style={{ ...boldRowStyle }}>
        <span>RETURN TOTAL</span><span>{money(receipt.returnTotal)}</span>
      </div>
      {parseFloat(receipt.promotionClawbackTotal || 0) > 0 && (
        <div style={{ ...rowStyle, fontSize: '9px' }}>
          <span>(Incl. promo reversed -{money(receipt.promotionClawbackTotal)}{receipt.debitNoteNumber ? ` / DN ${receipt.debitNoteNumber}` : ''})</span><span />
        </div>
      )}
      {receipt.refundMethod && (
        <div style={{ ...rowStyle, fontSize: '10px' }}>
          <span>Refund via</span><span>{receipt.refundMethod}</span>
        </div>
      )}

      {/* ── NEW SALE / TAX INVOICE section ── */}
      <div style={dividerStyle} />
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '11px' }}>
        --- NEW SALE / TAX INVOICE ---
      </div>
      {receipt.newInvoiceNumber && (
        <div style={{ fontSize: '11px', marginTop: '4px' }}>
          <div style={rowStyle}><span>Invoice</span><span>{receipt.newInvoiceNumber}</span></div>
        </div>
      )}

      <div style={solidDivider} />
      <ItemsTable
        items={receipt.newItems || []}
        amountLabel={receipt?.amountColumnLabel || 'Amount'}
        showRate={showRate}
        showAmt={showAmt}
        decimals={decimals}
        rateLabel={rateLabel}
      />
      <GstTable gstSummary={receipt.newSaleGstSummary} />

      <div style={dividerStyle} />
      <div style={boldRowStyle}>
        <span>NEW SALE TOTAL</span><span>{money(receipt.newSaleTotal)}</span>
      </div>
      {(receipt.payments || []).map((p, i) => (
        <div key={i} style={{ ...rowStyle, fontSize: '10px' }}>
          <span>Payment ({p.label})</span>
          <span>{money(p.amount)}</span>
        </div>
      ))}

      {/* ── SETTLEMENT SUMMARY ── */}
      <div style={dividerStyle} />
      <div style={{ fontWeight: 700, fontSize: '11px', textAlign: 'center', marginBottom: '4px' }}>
        SETTLEMENT SUMMARY
      </div>
      <div style={{ fontSize: '11px' }}>
        <div style={rowStyle}><span>Return value</span><span>{money(receipt.returnTotal)}</span></div>
        <div style={rowStyle}><span>New sale</span><span>{money(receipt.newSaleTotal)}</span></div>
        <div style={solidDivider} />
        {isEven ? (
          <div style={{ textAlign: 'center', fontWeight: 700 }}>No additional payment — even exchange</div>
        ) : isRefund ? (
          <div style={{ ...boldRowStyle }}>
            <span>Refund issued</span><span>{money(receipt.netSettlementAmount)}</span>
          </div>
        ) : (
          <div style={{ ...boldRowStyle }}>
            <span>Customer paid</span><span>{money(receipt.netSettlementAmount)}</span>
          </div>
        )}
      </div>

      <div style={dividerStyle} />
      <div style={{ textAlign: 'center', fontSize: '10px', lineHeight: 1.6 }}>
        Thank you for shopping with us!{'\n'}
        Exchange governed by our return policy.{'\n'}
        Keep this receipt.
      </div>
      {receipt.declarationText && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Declarations</div>
          <div style={{ fontSize: '9px', lineHeight: 1.5, textAlign: 'justify' }}>{receipt.declarationText}</div>
        </>
      )}

      {receipt.footerMessage && (
        <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, marginTop: '8px' }}>
          {receipt.footerMessage}
        </div>
      )}
      {receipt.websiteHandle && (
        <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '2px' }}>{receipt.websiteHandle}</div>
      )}
      {receipt.instagramHandle && (
        <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '2px' }}>{receipt.instagramHandle}</div>
      )}
    </div>
  );
};

export default ThermalExchangeReceipt80mm;
